import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { EmptyError, filter, firstValueFrom, map, merge, Observable, of, ReplaySubject, timeout, TimeoutError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Session, SessionInitializationResourceType } from '../../shared/models/identity.model';
import { SessionIDMessage, TrovataMessage, TrovataMessageType } from '../models/trovata-messaging.types';
import { UtilityText } from '../models/utility-text';
import { MessagingService } from './cross-app-messaging.service';
import { SnackbarService } from './snackbar.service';

const SESSION_ID_COOKIE_NAME: Readonly<string> = 'trvt_widget_session_id';
const UUID_REGEXP: Readonly<RegExp> = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SESSION_ID_TIMEOUT: number = 60_000;

@Injectable({
	providedIn: 'root',
})
export class SessionService {
	private document: Document = inject(DOCUMENT);
	private cookies: CookieService = inject(CookieService);
	private httpBackend: HttpBackend = inject(HttpBackend);
	private httpClient: HttpClient;
	private route: ActivatedRoute = inject(ActivatedRoute);
	private router: Router = inject(Router);
	private snackBar: SnackbarService = inject(SnackbarService);
	private crossAppMessaging: MessagingService = inject(MessagingService);

	private sessionSubject$: ReplaySubject<Session>;
	session$: Observable<Session>;

	private partnerIdSubject$: ReplaySubject<string>;
	partnerId$: Observable<string>;

	constructor() {
		// Instantiating custom HTTP client to bypass auth interceptor
		this.httpClient = new HttpClient(this.httpBackend);

		this.sessionSubject$ = new ReplaySubject<Session>(1);
		this.partnerIdSubject$ = new ReplaySubject<string>(1);

		this.session$ = this.sessionSubject$.asObservable();
		this.partnerId$ = this.partnerIdSubject$.asObservable();

		this.getSessionId()
			.then((sessionId: string) =>
				firstValueFrom(
					this.httpClient.get<Session>(environment.trovataApi + '/session', {
						headers: { Authorization: `Bearer ${sessionId}` },
					})
				)
			)
			.then((session: Session) => {
				if (this.isWellsFargoHost() && session.partner.id !== environment.wfPartnerId) {
					return this.redirectToWellsFargo();
				}

				this.sessionSubject$.next(session);
				this.setPartnerData(session);
				this.handleInitializationParams(session);
			})
			.catch((error: Error) => {
				// EmptyError: all session ID sources have completed and we didn't get a valid value from any of them
				// TimeoutError: we've waited over SESSION_ID_TIMEOUT ms for any session ID source to emit a valid value (but they haven't completed yet)
				// HttpErrorResponse: we got a valid value from one of the sources but the session is expired or doesn't exist
				if (error instanceof EmptyError || error instanceof TimeoutError || (error instanceof HttpErrorResponse && [401, 403].includes(error.status))) {
					if (this.isWellsFargoHost()) {
						return this.redirectToWellsFargo();
					}
				}

				console.error(error);
				this.sessionSubject$.error(error);
				this.snackBar.open(UtilityText.somethingWrongMsg, undefined, { duration: 6000 });
				this.router.navigate(['/session-expired']);
			});
	}

	async refreshSession(): Promise<Session> {
		try {
			const session: Session = await firstValueFrom(this.session$);
			const newSession: Session = await firstValueFrom(
				this.httpClient.patch<Session>(environment.trovataApi + '/session/refresh', {}, { headers: { Authorization: `Bearer ${session.id}` } })
			);

			this.sessionSubject$.next(newSession);
			return newSession;
		} catch (error) {
			console.error(error);
			this.sessionSubject$.error(error);
			throw error;
		}
	}

	private isWellsFargoHost(): boolean {
		return this.document.location.hostname === environment.wfWidgetHost;
	}

	private redirectToWellsFargo(): void {
		this.document.location.href = environment.wfLoginUrl;
	}

	private getSessionId(): Promise<string> {
		const sessionIdSources: Observable<string | null | undefined>[] = [of(this.cookies.get(SESSION_ID_COOKIE_NAME))];
		if (!this.isWellsFargoHost()) {
			sessionIdSources.push(
				this.route.queryParamMap.pipe(map((params: ParamMap) => params.get('sessionId'))),
				this.crossAppMessaging.incomingMessages$.pipe(
					filter((message: TrovataMessage | null): message is SessionIDMessage => message?.type === TrovataMessageType.SESSION_ID),
					map((message: SessionIDMessage) => message.sessionId)
				)
			);
		}

		return firstValueFrom(
			merge(...sessionIdSources).pipe(
				filter((text: string | null | undefined): text is string => !!text && UUID_REGEXP.test(text)),
				timeout(SESSION_ID_TIMEOUT)
			)
		);
	}

	private setPartnerData(session: Session): void {
		try {
			this.partnerIdSubject$.next(session.partner.id);
			this.snackBar.setPartnerConfig(session.partner.id);
			if (session.partner.id === environment.wfPartnerId) {
				window.parent?.postMessage({ type: 'trovata-set-iframe-square' }, '*');
			}
		} catch (error) {
			console.error('Failed to set partner from session', error);
		}
	}

	private handleInitializationParams(session: Session): void {
		if (!session.initializationParameters) {
			return;
		}

		if (session.initializationParameters.resourceType === SessionInitializationResourceType.connection) {
			this.router.navigate(['/'], {
				queryParams: {
					connectionId: session.initializationParameters.resourceId,
					action: session.initializationParameters.action,
					institutionId: session.initializationParameters.institutionId,
				},
			});
		}
	}
}
